/**
 * usePersonaReconcile — the always-on demo persona effects, lifted out of the old
 * PersonaSwitcher so they keep running even though the picker UI now lives inside
 * the Mode switcher dropdown (which is closed most of the time). Mounted once, in
 * the Header, so it runs in every mode/surface (the effects self-gate to demo).
 *
 * Three effects:
 *   0. Bootstrap the Supabase persona roster at shell mount, so the switcher
 *      shows the active company's real cast on every page (not just timesheet).
 *   1. Keep the header identity (name/role) in sync with the active employee —
 *      restores the persona after a refresh and mirrors a Settings-picker change.
 *   2. Reconcile a company/persona mismatch (the Renee Park / Boeing → wrong
 *      person refresh bug): an explicit company pick re-points the persona to that
 *      company's cast; a refresh/restore aligns the company to the persona instead.
 *
 * Logic is unchanged from the original PersonaSwitcher; only its home moved.
 */
import { useEffect } from 'react'
import { useAppStore, PERSIST_KEY_DEMO_PERSONA } from '@/store/appStore'
import { useTimesheetStore } from '@/modules/timesheet/stores/timesheetStore'
import { timesheetDataService } from '@/modules/timesheet/services/timesheetDataService'
import { scopeDemoCast } from '@/modules/timesheet/lib/demoCast'
import { consumeCompanyPick } from '@/modules/timesheet/lib/companyPickIntent'
import { getDemoCompany, getDemoCompanyByCastOrg } from '@/types/demoCompanies'
import { deriveSystemRole, deriveAppRole, workerSection, persistPersonaSnapshot, applyPersona, ensurePersonaRosterLoaded } from './personaCast'

export function usePersonaReconcile() {
  const appMode = useAppStore((s) => s.appMode)
  const referenceDataVersion = useAppStore((s) => s.referenceDataVersion)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const currentEmployeeId = useTimesheetStore((s) => s.currentEmployeeId)

  // 0. Load the Supabase persona roster once at shell mount (idempotent). Before
  //    this, only the timesheet routes provider bootstrapped it, so on PPM pages
  //    the switcher fell back to the mock seed cast (no cast org ids → company
  //    scoping silently ignored) until a timesheet page was visited.
  useEffect(() => {
    void ensurePersonaRosterLoaded()
  }, [])

  // 1. Keep the header identity + access override in sync with the active employee.
  useEffect(() => {
    if (appMode !== 'demo' || !currentEmployeeId) return
    const emp = timesheetDataService.getEmployee(currentEmployeeId)
    if (!emp) return
    const { currentUser, setCurrentUser, setTimesheetRoleOverride } = useAppStore.getState()
    if (currentUser.name === emp.name) return
    const systemRole = deriveSystemRole(emp)
    const appRole = deriveAppRole(systemRole)
    setCurrentUser({ ...currentUser, id: emp.id, name: emp.name, role: appRole as typeof currentUser.role, initials: emp.initials })
    setTimesheetRoleOverride(systemRole)
    persistPersonaSnapshot(emp, systemRole, appRole)
  }, [appMode, currentEmployeeId])

  // 2. Reconcile a company/persona mismatch (intent-driven).
  useEffect(() => {
    if (appMode !== 'demo') return
    const activeId = useTimesheetStore.getState().currentEmployeeId
    if (!activeId) return
    const activeEmp = timesheetDataService.getEmployee(activeId)
    if (!activeEmp) return
    const company = getDemoCompany(currentCompanyId)
    // Persona already belongs to the active company's cast — nothing to do.
    if (company?.castOrgId && activeEmp.primaryOrganizationId === company.castOrgId) {
      consumeCompanyPick()
      return
    }
    // Was the current company set by a deliberate pick (vs a refresh/restore)?
    const explicitlyPicked = consumeCompanyPick() === currentCompanyId
    if (explicitlyPicked && company?.castOrgId) {
      // Deliberate company switch → bring the persona to the new company's cast.
      const cast = scopeDemoCast(timesheetDataService.getEmployees(), currentCompanyId)
      if (cast.length === 0) {
        // Picked company has NO timesheet cast yet (e.g. Codan) → clear to a neutral
        // identity instead of bleeding the prior company's persona.
        const { currentUser: cu, setCurrentUser, setTimesheetRoleOverride } = useAppStore.getState()
        setCurrentUser({ ...cu, id: 'demo_user', name: 'Demo User', role: 'Viewer', initials: 'DU' })
        setTimesheetRoleOverride(null)
        useTimesheetStore.getState().setCurrentEmployeeId('')
        try { localStorage.removeItem(PERSIST_KEY_DEMO_PERSONA) } catch { /* ignore */ }
        return
      }
      const next = cast.find((e) => workerSection(e) === workerSection(activeEmp)) ?? cast[0]
      if (next && next.id !== activeId) applyPersona(next)
      return
    }
    // Refresh / restore with a mismatch → the PERSONA wins: align the company to it.
    if (!currentCompanyId) return
    const personaCompany = getDemoCompanyByCastOrg(activeEmp.primaryOrganizationId)
    if (personaCompany && personaCompany.id !== currentCompanyId) {
      useAppStore.getState().setCurrentCompany(personaCompany.id)
    }
  }, [appMode, currentCompanyId, referenceDataVersion])
}
